import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronBack, IoChevronForward, IoClose } from "react-icons/io5";
import { FaHeartbeat, FaLungs, FaThermometerHalf, FaTint } from "react-icons/fa";
import Backdrop from "@mui/material/Backdrop";
import FamilyHeader from "./FamilyHeader";
import { getSessionContext } from "../../common/utils/sessionContext";
import { getVitalsOfFamilyMember } from "../../common/api/familyportalApi";
import * as Sentry from "@sentry/react";

const parseVitalInformation = (raw) => {
  if (!raw) {
    return {};
  }
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return {};
  }
};

const parseCreatedDate = (createdDate) => {
  const raw = String(createdDate || "").trim();
  if (!raw) {
    return { dateKey: "", timeLabel: "" };
  }

  const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(.+)$/);
  if (!match) {
    return { dateKey: "", timeLabel: raw };
  }

  const [, month, day, year, timeLabel] = match;
  return {
    dateKey: `${year}-${month}-${day}`,
    timeLabel: timeLabel.trim(),
  };
};

const vitalPayloadToFormState = (parsed) => {
  if (!parsed || typeof parsed !== "object") {
    return {
      date: "",
      time: "",
      leftArmBP: "",
      rightArmBP: "",
      currentHeartRate: "",
      restingHeartRate: "",
      bodyTemp: "",
      bloodSugar: "",
      oxygenLevel: "",
      weight: "",
    };
  }
  const s = (v) => (v == null || v === "" ? "" : String(v));
  return {
    date: s(parsed.date),
    time: s(parsed.time),
    leftArmBP: s(parsed.leftArmBP ?? parsed.leftArmBp),
    rightArmBP: s(parsed.rightArmBP ?? parsed.rightArmBp),
    currentHeartRate: s(parsed.currentHeartRate),
    restingHeartRate: s(parsed.restingHeartRate),
    bodyTemp: s(parsed.bodyTemp),
    bloodSugar: s(parsed.bloodSugar),
    oxygenLevel: s(parsed.oxygenLevel),
    weight: s(parsed.weight),
  };
};

const formatVitalTimeLabel = (time24) => {
  if (!time24) {
    return "";
  }
  const [hStr, mStr] = time24.split(":");
  const h = Number(hStr);
  const m = mStr ?? "00";
  if (Number.isNaN(h)) {
    return time24;
  }
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${m} ${period}`;
};

const enrichVitalRecord = (record, index) => {
  const parsed = parseVitalInformation(record.vitalInformation);
  const { dateKey, timeLabel } = parseCreatedDate(record.createdDate);

  return {
    ...record,
    id: `${record.clientId || "client"}-${dateKey}-${timeLabel}-${index}`,
    parsed,
    dateKey,
    timeLabel: timeLabel || formatVitalTimeLabel(parsed.time),
    createdDateLabel: record.createdDate || "",
    caregiverName: record.caregiverName || "",
  };
};

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const getDateTimeParts = (dateKey, timeLabel) => {
  if (!dateKey || dateKey === "unknown") {
    return { month: "—", day: "—", year: "", time: timeLabel || "" };
  }
  const [year, month, day] = dateKey.split("-").map((part) => Number(part));
  if (!year || !month || !day) {
    return { month: "—", day: "—", year: "", time: timeLabel || "" };
  }
  return {
    month: MONTH_SHORT[month - 1] || "",
    day: String(day),
    year: String(year),
    time: timeLabel || "",
  };
};

const formatVitalCardSummary = (parsed) => {
  const left = parsed?.leftArmBP;
  const right = parsed?.rightArmBP;
  let bp = "—";
  if (left && right) {
    bp = `${left}/${right}`;
  } else if (left) {
    bp = String(left);
  } else if (right) {
    bp = String(right);
  }

  const hr = parsed?.currentHeartRate ? `${parsed.currentHeartRate} bpm` : "—";
  const spo2 = parsed?.oxygenLevel ? `${parsed.oxygenLevel}%` : "—";
  const temp = parsed?.bodyTemp ? `${parsed.bodyTemp}°F` : "—";

  return { bp, hr, spo2, temp };
};

const buildVitalsHistory = (records = []) => {
  const enriched = records.map(enrichVitalRecord).sort((a, b) => {
    const dateCompare = b.dateKey.localeCompare(a.dateKey);
    if (dateCompare !== 0) {
      return dateCompare;
    }
    return (b.timeLabel || "").localeCompare(a.timeLabel || "");
  });

  return {
    records: enriched,
    totalCount: enriched.length,
    hasData: enriched.length > 0,
  };
};

const METRICS = [
  { key: "bp", label: "BP", icon: FaTint },
  { key: "hr", label: "HR", icon: FaHeartbeat },
  { key: "spo2", label: "SpO2", icon: FaLungs },
  { key: "temp", label: "Temp", icon: FaThermometerHalf },
];

const VitalHistoryCard = ({ record, summary, dateParts, onSelect }) => (
  <button type="button" className="family-vitals-history-card" onClick={() => onSelect(record)}>
    <div className="family-vitals-history-date">
      <span className="family-vitals-history-month">{dateParts.month}</span>
      <span className="family-vitals-history-day">{dateParts.day}</span>
      <span className="family-vitals-history-year">{dateParts.year}</span>
      <span className="family-vitals-history-time">{dateParts.time}</span>
    </div>
    <div className="family-vitals-history-body">
      <div className="family-vitals-history-metrics">
        {METRICS.map(({ key, label, icon: Icon }) => (
          <div key={key} className="family-vitals-metric">
            <span className="family-vitals-metric-icon" aria-hidden="true">
              <Icon />
            </span>
            <span className="family-vitals-metric-label">{label}</span>
            <span className="family-vitals-metric-value">{summary[key]}</span>
          </div>
        ))}
        <IoChevronForward className="family-vitals-history-chevron" aria-hidden="true" />
      </div>
    </div>
  </button>
);

const DetailRow = ({ label, value }) => (
  <div className="family-vitals-detail-row">
    <span className="family-vitals-detail-label">{label}</span>
    <span className="family-vitals-detail-value">{value || "—"}</span>
  </div>
);

// buildVitalsHistory sorts newest-first, so records[0] is the latest reading.
// Used by the home page for the "Last reading …" line on the Vitals tab.
export const summarizeLatestVital = (apiData) => {
  const { records } = buildVitalsHistory(Array.isArray(apiData) ? apiData : []);
  return records[0] || null;
};

const FamilyVitals = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [sheetVisible, setSheetVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [historyData, setHistoryData] = useState({ records: [], hasData: false, totalCount: 0 });

  const { familyClientId, familyMemberClientId, familyClientName } = getSessionContext();
  const clientId = familyClientId || familyMemberClientId;
  const vitalForm = selectedRecord ? vitalPayloadToFormState(selectedRecord.parsed) : null;
  const selectedDateParts = selectedRecord ? getDateTimeParts(selectedRecord.dateKey, selectedRecord.timeLabel) : null;
  const vitalDateLabel = selectedDateParts?.month && selectedDateParts?.day && selectedDateParts?.year ? `${selectedDateParts.month} ${selectedDateParts.day}, ${selectedDateParts.year}` : vitalForm?.date || "";

  useEffect(() => {
    let cancelled = false;

    const fetchVitals = async () => {
      if (!clientId) {
        setHistoryData({ records: [], hasData: false, totalCount: 0 });
        setError("Please select a client to view vitals.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const result = await getVitalsOfFamilyMember(clientId);
        if (cancelled) {
          return;
        }

        if (!result.success) {
          setHistoryData({ records: [], hasData: false, totalCount: 0 });
          setError(result.message || "Failed to load vitals.");
          return;
        }

        setHistoryData(buildVitalsHistory(result.data));
      } catch (error) {
        Sentry.captureException(error);
        if (!cancelled) {
          setHistoryData({ records: [], hasData: false, totalCount: 0 });
          setError("Failed to load vitals.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchVitals();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const openDetail = (record) => {
    setSelectedRecord(record);
    setSheetVisible(true);
  };

  const closeDetail = () => {
    setSheetVisible(false);
  };

  useEffect(() => {
    if (!sheetVisible) {
      const timer = window.setTimeout(() => setSelectedRecord(null), 300);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [sheetVisible]);

  useEffect(() => {
    if (!sheetVisible) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeDetail();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [sheetVisible]);

  return (
    <div className={embedded ? "family-vitals-layout fh2-embed" : "family-page-layout family-vitals-layout"}>
      {embedded ? null : <FamilyHeader clientName={familyClientName} />}
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
        <img src={require("../../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>

      {embedded ? null : (
        <div className="family-page-subheader">
          <div className="family-checklist-topbar">
            <button type="button" className="family-checklist-back" onClick={() => navigate("/family-home")} aria-label="Back to home">
              <IoChevronBack />
            </button>
            <h1 className="family-checklist-title-center">Vitals</h1>
            <span className="family-vitals-topbar-spacer" aria-hidden="true" />
          </div>
        </div>
      )}

      <div className="family-vitals-page">
        <div className="family-vitals-body">
          {!loading && error ? (
            <div className="family-checklist-empty-date">
              <p>{error}</p>
            </div>
          ) : null}

          {!loading && !error && historyData.hasData ? (
            <>
              <h2 className="family-vitals-history-heading">History</h2>
              <div className="family-vitals-history-list">
                {historyData.records.map((record) => (
                  <VitalHistoryCard key={record.id} record={record} summary={formatVitalCardSummary(record.parsed)} dateParts={getDateTimeParts(record.dateKey, record.timeLabel)} onSelect={openDetail} />
                ))}
              </div>
            </>
          ) : null}

          {!loading && !error && !historyData.hasData ? (
            <div className="family-checklist-empty-date">
              <p>No vital records available yet.</p>
            </div>
          ) : null}
        </div>
      </div>

      {selectedRecord && vitalForm ? (
        <>
          <button type="button" className={`family-vitals-sheet-backdrop ${sheetVisible ? "is-open" : ""}`} aria-label="Close vital details" onClick={closeDetail} />
          <div className={`family-vitals-sheet ${sheetVisible ? "is-open" : ""}`} role="dialog" aria-modal="true" aria-labelledby="family-vitals-sheet-title">
            <div className="family-vitals-sheet-handle" aria-hidden="true" />
            <div className="family-vitals-sheet-header">
              <div>
                <h2 id="family-vitals-sheet-title" className="family-vitals-sheet-title">
                  Vital details
                </h2>
              </div>
              <button type="button" className="family-vitals-sheet-close" onClick={closeDetail} aria-label="Close">
                <IoClose />
              </button>
            </div>

            <div className="family-vitals-sheet-body">
              <section className="family-vitals-detail-section">
                <h3 className="family-vitals-detail-section-title">Report date &amp; time</h3>
                <div className="family-vitals-detail-row family-vitals-detail-row--datetime">
                  <div className="family-vitals-detail-datetime-part">
                    <span className="family-vitals-detail-label">Date</span>
                    <span className="family-vitals-detail-value">{vitalDateLabel || "—"}</span>
                  </div>
                  <div className="family-vitals-detail-datetime-part">
                    <span className="family-vitals-detail-label">Time</span>
                    <span className="family-vitals-detail-value">{selectedDateParts?.time || vitalForm.time || "—"}</span>
                  </div>
                </div>
                <DetailRow label="Reported by" value={selectedRecord.caregiverName} />
              </section>
              <section className="family-vitals-detail-section">
                <h3 className="family-vitals-detail-section-title">Blood pressure</h3>
                <DetailRow label="Left arm" value={vitalForm.leftArmBP} />
                <DetailRow label="Right arm" value={vitalForm.rightArmBP} />
              </section>

              <section className="family-vitals-detail-section">
                <h3 className="family-vitals-detail-section-title">Heart rate</h3>
                <DetailRow label="Current rate" value={vitalForm.currentHeartRate ? `${vitalForm.currentHeartRate} bpm` : ""} />
                <DetailRow label="Resting rate" value={vitalForm.restingHeartRate ? `${vitalForm.restingHeartRate} bpm` : ""} />
              </section>

              <section className="family-vitals-detail-section">
                <h3 className="family-vitals-detail-section-title">Other vitals</h3>
                <DetailRow label="Body temperature" value={vitalForm.bodyTemp ? `${vitalForm.bodyTemp}°F` : ""} />
                <DetailRow label="Blood glucose" value={vitalForm.bloodSugar ? `${vitalForm.bloodSugar} mmol/L` : ""} />
                <DetailRow label="Oxygen level" value={vitalForm.oxygenLevel ? `${vitalForm.oxygenLevel}%` : ""} />
                <DetailRow label="Weight" value={vitalForm.weight ? `${vitalForm.weight} lbs` : ""} />
              </section>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default FamilyVitals;
